const STORAGE_KEY = 'partner_affiliate_attribution_v1';
const COOKIE_NAME = 'partner_affiliate_attribution_v1';
export const PARTNER_ATTRIBUTION_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
export const TRACKING_CODE_EXPLICIT_KEYS = ['tracking_code'];
export const TRACKING_CODE_ALIAS_KEYS = ['ref', 'code', 'partner_code'];
export const TRACKING_CODE_QUERY_KEYS = [...TRACKING_CODE_EXPLICIT_KEYS, ...TRACKING_CODE_ALIAS_KEYS];

export const trackingCodeFromSearch = (search = '', { allowAliases = false } = {}) => {
  const raw = String(search || '');
  const query = raw.startsWith('?') ? raw.slice(1) : raw;
  if (!query) return '';
  const params = new URLSearchParams(query);
  const keys = allowAliases ? TRACKING_CODE_QUERY_KEYS : TRACKING_CODE_EXPLICIT_KEYS;
  for (const key of keys) {
    const value = String(params.get(key) || '').trim();
    if (value) return value;
  }
  return '';
};

export const isAttributionFresh = (parsed, now = Date.now(), maxAgeMs = PARTNER_ATTRIBUTION_MAX_AGE_MS) => {
  if (!parsed || typeof parsed !== 'object') return false;
  const savedAt = Number(parsed.saved_at || 0);
  if (!savedAt) return false;
  return now - savedAt <= maxAgeMs;
};

export const normalizePartnerAttribution = (data = {}, savedAt = Date.now()) => ({
  partner_id: data?.partner_id ? Number(data.partner_id) : null,
  partner_slug: data?.partner_slug ? String(data.partner_slug) : '',
  partner_name: data?.partner_name ? String(data.partner_name) : '',
  partner_color: data?.partner_color ? String(data.partner_color) : '',
  tracking_code: data?.tracking_code ? String(data.tracking_code) : '',
  seller_id: data?.seller_id ? Number(data.seller_id) : null,
  accommodation_id: data?.accommodation_id ? Number(data.accommodation_id) : null,
  accommodation_name: data?.accommodation_name ? String(data.accommodation_name) : '',
  accommodation_region_id: data?.accommodation_region_id ? Number(data.accommodation_region_id) : null,
  portal_default_delivery_ref: data?.portal_default_delivery_ref ? String(data.portal_default_delivery_ref) : '',
  portal_default_collection_ref: data?.portal_default_collection_ref ? String(data.portal_default_collection_ref) : '',
  website_url: data?.website_url ? String(data.website_url) : '',
  logo_url: data?.logo_url ? String(data.logo_url) : '',
  saved_at: Number(savedAt) || Date.now(),
});

const samePartner = (left, right) => {
  const leftId = Number(left?.partner_id || 0);
  const rightId = Number(right?.partner_id || 0);
  if (leftId > 0 && rightId > 0) return leftId === rightId;
  const leftSlug = String(left?.partner_slug || '').trim().toLowerCase();
  const rightSlug = String(right?.partner_slug || '').trim().toLowerCase();
  return Boolean(leftSlug && rightSlug && leftSlug === rightSlug);
};

const ATTRIBUTION_CONTENT_KEYS = [
  'partner_id',
  'partner_slug',
  'partner_name',
  'partner_color',
  'tracking_code',
  'seller_id',
  'accommodation_id',
  'accommodation_name',
  'accommodation_region_id',
  'portal_default_delivery_ref',
  'portal_default_collection_ref',
  'website_url',
  'logo_url',
];

export const attributionContentEquals = (left, right) => {
  if (left === right) return true;
  if (!left || !right) return false;
  return ATTRIBUTION_CONTENT_KEYS.every((key) => String(left[key] ?? '') === String(right[key] ?? ''));
};

export const shouldPersistAttribution = ({
  existing = null,
  next = null,
  partnerSlug = '',
  search = '',
} = {}) => {
  if (!next) return false;
  if (String(partnerSlug || '').trim()) return true;
  if (trackingCodeFromSearch(search)) return true;
  return !attributionContentEquals(existing, next);
};

export const mergePartnerAttribution = (existing, incoming, urlTrackingCode = '') => {
  const next = { ...(incoming || {}) };
  const urlCode = String(urlTrackingCode || '').trim();
  const incomingCode = String(next.tracking_code || '').trim();
  const existingCode = samePartner(existing, next) ? String(existing?.tracking_code || '').trim() : '';

  next.tracking_code = urlCode || incomingCode || existingCode || '';

  if (urlCode && urlCode !== incomingCode) {
    next.seller_id = null;
  } else if (next.seller_id == null && samePartner(existing, next)) {
    next.seller_id = existing?.seller_id ?? null;
  }

  return normalizePartnerAttribution(next);
};

const writeCookie = (payload) => {
  if (typeof document === 'undefined') return;
  try {
    const maxAge = Math.floor(PARTNER_ATTRIBUTION_MAX_AGE_MS / 1000);
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  } catch {
    // Ignore cookie quota / disabled cookie failures.
  }
};

const readCookie = () => {
  if (typeof document === 'undefined') return null;
  try {
    const parts = String(document.cookie || '').split(';');
    for (const part of parts) {
      const [rawName, ...rest] = part.split('=');
      if (String(rawName || '').trim() !== COOKIE_NAME) continue;
      const raw = decodeURIComponent(rest.join('=').trim());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    }
  } catch {
    return null;
  }
  return null;
};

const clearCookie = () => {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
};

const persistStores = (payload) => {
  if (typeof window === 'undefined') return payload;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures; cookie still covers refresh.
  }
  writeCookie(payload);
  return payload;
};

export const readPartnerAttribution = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isAttributionFresh(parsed)) return parsed;
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Fall through to cookie.
  }

  const fromCookie = readCookie();
  if (isAttributionFresh(fromCookie)) {
    return persistStores(fromCookie);
  }
  if (fromCookie) clearCookie();
  return null;
};

export const savePartnerAttribution = (data) => {
  const payload = normalizePartnerAttribution(data);
  return persistStores(payload);
};

export const clearPartnerAttribution = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
  clearCookie();
};

export const preparePartnerAttribution = async ({
  partnerSlug = '',
  search = '',
  resolvePartner,
} = {}) => {
  const slug = String(partnerSlug || '').trim();
  const urlCode = trackingCodeFromSearch(search, { allowAliases: Boolean(slug) });
  const existing = readPartnerAttribution();

  if (!slug) {
    if (urlCode && existing) {
      return normalizePartnerAttribution({ ...existing, tracking_code: urlCode, seller_id: null });
    }
    return existing;
  }

  let resolved = null;
  if (typeof resolvePartner === 'function') {
    resolved = await resolvePartner(slug);
  }
  if (!resolved?.partner_id && !resolved?.partner_slug) {
    if (urlCode && existing && samePartner(existing, { partner_slug: slug })) {
      return normalizePartnerAttribution({ ...existing, tracking_code: urlCode, seller_id: null });
    }
    return existing;
  }

  return mergePartnerAttribution(existing, {
    ...resolved,
    partner_slug: resolved.partner_slug || slug,
  }, urlCode);
};

export const commitPreparedAttribution = ({
  existing = null,
  next = null,
  partnerSlug = '',
  search = '',
} = {}) => {
  if (!next) return next;
  if (!shouldPersistAttribution({ existing, next, partnerSlug, search })) {
    return existing || next;
  }
  return savePartnerAttribution(next);
};

export const ensurePartnerAttribution = async (opts = {}) => {
  const existing = readPartnerAttribution();
  const next = await preparePartnerAttribution(opts);
  if (!next || opts.persist === false) return next;
  return commitPreparedAttribution({
    existing,
    next,
    partnerSlug: opts.partnerSlug,
    search: opts.search,
  });
};
